import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'components/context/AuthContext'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import {
  OnboardingValidateEmailStep,
  OnboardingUserTypeStep,
  OnboardingNameAndPhoneStep,
  OnboardingWebsiteStep,
  OnboardingBusinessStep,
  EsporaiButton,
  EsporaiVideo,
  LoadingScreen
} from 'components';
import { usersApi, onboardingApi, brandsApi } from 'api';
import { toast } from 'react-hot-toast';

const STEP_KEYS = {
  USER_TYPE: 'user_type',
  VALIDATE_MAIL: 'validate_mail',
  BUSINESS_WEBSITE: 'business_website',
  BUSINESS: 'business',
  NAME_AND_PHONE: 'name_and_phone',
};

const Onboarding = () => {
  const navigate = useNavigate();
  const didInitRef = useRef(false);
  const didCheckOnboardingRef = useRef(false);
  const { updateContextUser, logout, auth } = useAuth();
  const [stepSequence, setStepSequence] = useState([STEP_KEYS.USER_TYPE, STEP_KEYS.NAME_AND_PHONE]);
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingUserData, setLoadingUserData] = useState(true);
  const [formData, setFormData] = useState({
    user_type: '',
    first_name: '',
    phoneNumber: '',
    prefix: '+598',
    website_url: '',
    business_name: '',
    business_details: '',
    google_ads_connected: false,
    meta_connected: false
  });
  const [stepsValid, setStepsValid] = useState({});
  const [scrapeError, setScrapeError] = useState(false);
  const [isScraping, setIsScraping] = useState(false);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    const init = async () => {
      try {
        setLoadingUserData(true);
        const res = await usersApi.getCurrentUser();
        if (res?.data) {
          const u = res.data;
          const verified = Boolean(u.email_verified);
          const userType = (u.user_type || '').toString().trim().toLowerCase();
          const hasChosenType = userType === 'agencia' || userType === 'negocio';
          const afterUserType = hasChosenType
            ? (userType === 'negocio'
                ? [STEP_KEYS.NAME_AND_PHONE, STEP_KEYS.BUSINESS_WEBSITE, STEP_KEYS.BUSINESS]
                : [STEP_KEYS.NAME_AND_PHONE])
            : [];
          const base = verified
            ? [STEP_KEYS.USER_TYPE, ...afterUserType]
            : [STEP_KEYS.VALIDATE_MAIL, STEP_KEYS.USER_TYPE, ...afterUserType];
          setStepSequence(base);
          setFormData({
            user_type: u.user_type || '',
            first_name: u.first_name || '',
            phoneNumber: u.phone_number || '',
            prefix: u.prefix || '+598',
            website_url: u.website_url || '',
            business_name: u.business_name || '',
            business_details: u.business_details || '',
            google_ads_connected: u.google_ads_connected || false,
          });
          if (updateContextUser) {
            await updateContextUser(u);
          }
        }
      } catch (err) {
        console.error(err);
        const errorMessage = err.response?.data?.message || err?.message || 'Error al cargar los datos del usuario';
        toast.error(`Error al cargar los datos del usuario: ${errorMessage}`);
      } finally {
        setLoadingUserData(false);
      }
    };

    init();
  }, [updateContextUser]);

  // Secuencia de pasos: nombre y teléfono siempre después de elegir Agencia o Negocio.
  // Pasos de negocio (URL + datos) van al final, después de nombre y WhatsApp.
  useEffect(() => {
    if (loadingUserData) return;
    const hasValidateMail = stepSequence.includes(STEP_KEYS.VALIDATE_MAIL);
    const isNegocio = (formData.user_type || '').toLowerCase() === 'negocio';
    const isAgencia = (formData.user_type || '').toLowerCase() === 'agencia';
    const hasChosenType = isNegocio || isAgencia;

    const afterUserType = hasChosenType
      ? (isNegocio
          ? [STEP_KEYS.NAME_AND_PHONE, STEP_KEYS.BUSINESS_WEBSITE, STEP_KEYS.BUSINESS]
          : [STEP_KEYS.NAME_AND_PHONE])
      : [];

    const nextSequence = hasValidateMail
      ? [STEP_KEYS.VALIDATE_MAIL, STEP_KEYS.USER_TYPE, ...afterUserType]
      : [STEP_KEYS.USER_TYPE, ...afterUserType];

    setStepSequence(prev => {
      if (prev.length === nextSequence.length && prev.every((s, i) => s === nextSequence[i])) return prev;
      return nextSequence;
    });
  }, [formData.user_type, loadingUserData, stepSequence]);

  // Si la secuencia se acortó (ej. usuario pasó de negocio a agencia), ajustar stepIndex
  useEffect(() => {
    if (stepIndex >= stepSequence.length && stepSequence.length > 0) {
      setStepIndex(Math.max(0, stepSequence.length - 1));
    }
  }, [stepSequence.length, stepIndex]);

  useEffect(() => {
    if (!auth.token) {
      navigate('/login');
      return;
    }
    if (didCheckOnboardingRef.current) return;
    didCheckOnboardingRef.current = true;

    if (auth.user?.onboarding_completed) {
      navigate('/home');
    }
  }, [navigate, auth.token, auth.user]);

  const saveProgress = useCallback(async () => {
    const payload = {
      userType: typeof formData.user_type === 'string' ? formData.user_type.trim().toLowerCase() : formData.user_type,
      first_name: formData.first_name,
      phoneNumber: formData.phoneNumber,
      prefix: formData.prefix,
      googleConnected: formData.google_ads_connected,
      metaConnected: formData.meta_connected,
    };
    if (formData.website_url != null && formData.website_url !== '') {
      payload.websiteUrl = formData.website_url.trim().startsWith('http') ? formData.website_url.trim() : 'https://' + formData.website_url.trim();
    }
    if (formData.business_name != null && formData.business_name !== '') payload.business_name = formData.business_name;
    if (formData.business_details != null && formData.business_details !== '') payload.business_details = formData.business_details;
    try {
      const { data } = await usersApi.updateUser(payload);
      if (data) {
        await updateContextUser(data);
      }
      setFormData(prev => ({ ...prev, ...data }));
      return true;
    } catch (err) {
      const errorMessage = `Error al guardar el progreso: ${err.response?.data?.message || err?.message || 'Error desconocido'}`;
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  }, [formData, updateContextUser]);

  const completeOnboarding = useCallback(async () => {
    const payload = {
      userType: typeof formData.user_type === 'string' ? formData.user_type.trim().toLowerCase() : formData.user_type,
      first_name: formData.first_name,
      phoneNumber: formData.phoneNumber,
      prefix: formData.prefix,
      googleConnected: formData.google_ads_connected,
      metaConnected: formData.meta_connected,
    };
    if (formData.website_url != null && formData.website_url !== '') {
      payload.websiteUrl = formData.website_url.trim().startsWith('http') ? formData.website_url.trim() : 'https://' + formData.website_url.trim();
    }
    if (formData.business_name != null && formData.business_name !== '') payload.business_name = formData.business_name;
    if (formData.business_details != null && formData.business_details !== '') payload.business_details = formData.business_details;
    try {
      const res = await onboardingApi.completeOnboarding(payload);
      if (res.data.user) {
        await updateContextUser(res.data.user);
      }
      setFormData(prev => ({ ...prev, ...res.data.user }));
      return true;
    } catch (err) {
      const errorMessage = `Error al completar el onboarding: ${err.response?.data?.message || err?.message || 'Error desconocido'}`;
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  }, [formData, updateContextUser]);

  const currentStep = stepSequence[stepIndex];

  const handleStepValidation = (isValid) => {
    setStepsValid(prev => ({ ...prev, [currentStep]: isValid }));
    if (currentStep === STEP_KEYS.VALIDATE_MAIL && isValid) {
      handleNextStep(true);
    }
  };

  const handleNextStep = useCallback(async isValid => {
    if (!isValid) return;
    try {
      setLoading(true);
      setScrapeError(false);

      if (currentStep === STEP_KEYS.BUSINESS_WEBSITE) {
        const url = (formData.website_url || '').trim();
        const fullUrl = url.startsWith('http') ? url : 'https://' + url;
        setIsScraping(true);
        try {
          const { data } = await brandsApi.scrapeWebsite(fullUrl);
          const nextForm = {
            ...formData,
            business_name: data.business_name || formData.business_name,
            business_details: data.business_details || formData.business_details,
            website_url: fullUrl,
          };
          setFormData(nextForm);
          const payload = {
            userType: (nextForm.user_type || '').toString().trim().toLowerCase(),
            first_name: nextForm.first_name,
            phoneNumber: nextForm.phoneNumber,
            prefix: nextForm.prefix,
            websiteUrl: fullUrl,
            business_name: nextForm.business_name || '',
            business_details: nextForm.business_details || '',
          };
          const { data: userData } = await usersApi.updateUser(payload);
          if (userData) await updateContextUser(userData);
          setFormData(prev => ({ ...prev, ...userData }));
          setStepIndex(i => i + 1);
        } catch (err) {
          const isInvalidUrl = err?.response?.data?.error_code === 'INVALID_URL';
          setScrapeError(isInvalidUrl);
          toast.error(err?.response?.data?.error || err?.message || 'Error al analizar la URL');
          return;
        } finally {
          setIsScraping(false);
        }
        return;
      }

      if (stepIndex === stepSequence.length - 1) {
        await completeOnboarding();
        navigate('/home', { state: { startTour: true } });
      } else {
        await saveProgress();
        setStepIndex(i => i + 1);
      }
    } catch (err) {
      toast.error('Error al avanzar al siguiente paso. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [currentStep, formData, stepIndex, stepSequence.length, completeOnboarding, navigate, saveProgress, updateContextUser]);

  const handlePrevStep = () => {
    if (stepIndex === 0) {
      logout();
    } else {
      setStepIndex(i => i - 1);
    }
  };

  useEffect(() => {
    const onEnter = e => {
      if (
        e.key === 'Enter' &&
        !loadingUserData &&
        stepsValid[currentStep] &&
        currentStep !== STEP_KEYS.VALIDATE_MAIL
      ) {
        e.preventDefault();
        handleNextStep(true);
      }
    };
    document.addEventListener('keydown', onEnter);
    return () => document.removeEventListener('keydown', onEnter);
  }, [currentStep, stepIndex, stepsValid, handleNextStep, loadingUserData]);

  const updateFormData = useCallback((field, val) => {
    setFormData(fd => (fd[field] === val ? fd : { ...fd, [field]: val }));
  }, []);

  const renderCurrentStep = () => {
    switch (currentStep) {
      case STEP_KEYS.USER_TYPE:
        return (
          <OnboardingUserTypeStep
            value={formData.user_type}
            onChange={v => updateFormData('user_type', v)}
            onValidation={handleStepValidation}
          />
        );
      case STEP_KEYS.VALIDATE_MAIL:
        return <OnboardingValidateEmailStep onValidation={handleStepValidation} />;
      case STEP_KEYS.BUSINESS_WEBSITE:
        return (
          <OnboardingWebsiteStep
            value={formData.website_url}
            onChange={v => {
              updateFormData('website_url', v);
              if (scrapeError) setScrapeError(false);
            }}
            onValidation={handleStepValidation}
            userType="negocio"
            isProcessing={isScraping}
            invalidURLError={scrapeError}
          />
        );
      case STEP_KEYS.BUSINESS:
        return (
          <OnboardingBusinessStep
            business_name={formData.business_name}
            business_details={formData.business_details}
            onChange={payload => {
              if (payload.business_name !== undefined) updateFormData('business_name', payload.business_name);
              if (payload.business_details !== undefined) updateFormData('business_details', payload.business_details);
            }}
            onValidation={handleStepValidation}
          />
        );
      case STEP_KEYS.NAME_AND_PHONE:
        return (
          <OnboardingNameAndPhoneStep
            nameValue={formData.first_name}
            phoneValue={formData.phoneNumber}
            prefixValue={formData.prefix}
            onNameChange={v => updateFormData('first_name', v)}
            onPhoneChange={v => updateFormData('phoneNumber', v)}
            onPrefixChange={v => updateFormData('prefix', v)}
            onValidation={handleStepValidation}
          />
        );
      default:
        return null;
    }
  };

  const renderNavigationButtons = () => {
    if (currentStep === STEP_KEYS.VALIDATE_MAIL) return null;
    const isBusinessWebsiteStep = currentStep === STEP_KEYS.BUSINESS_WEBSITE;
    const nextDisabled = !stepsValid[currentStep] || loading || (isBusinessWebsiteStep && isScraping);
    const nextLabel = isBusinessWebsiteStep && isScraping
      ? 'Analizando...'
      : stepIndex === stepSequence.length - 1
        ? '¡Finalizar Onboarding!'
        : 'Siguiente';
    return (
      <div className="mt-6 space-y-4">
        <EsporaiButton
          type="button"
          className="w-full flex justify-center items-center"
          variant="primary"
          onClick={() => handleNextStep(true)}
          disabled={nextDisabled}
        >
          {loading && !isScraping ? 'Cargando...' : nextLabel}
          <ArrowRight className="ml-2" />
        </EsporaiButton>
        <EsporaiButton
          type="button"
          className="w-full flex justify-center items-center"
          variant="outline"
          onClick={stepIndex === 0 ? logout : handlePrevStep}
        >
          <ArrowLeft className="mr-2" /> {stepIndex === 0 ? 'Cerrar Sesión' : 'Atrás'}
        </EsporaiButton>
      </div>
    );
  };

  if (loadingUserData) {
    return <LoadingScreen />;
  }

  const TOTAL_STEPS = stepSequence.length;
  const progress = ((stepIndex + 1) / TOTAL_STEPS) * 100;

  return (
    <div className="onboarding-light min-h-screen flex flex-col md:flex-row relative overflow-hidden">
      <div className="absolute top-6 sm:top-8 left-1/2 transform -translate-x-1/2 z-50">
        <img src="/ch3.png" alt="Logo" className="h-12 sm:h-16 w-auto" />
      </div>
      <div className="w-full md:w-1/2 flex flex-col justify-center p-4 pt-20 sm:pt-24 md:p-8 min-h-screen bg-white">
        <div className="mb-6 lg:ml-20">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="h-2 rounded-full bg-blue-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Paso {stepIndex + 1} de {TOTAL_STEPS}
          </p>
        </div>
        <div className="w-full ml-auto overflow-y-auto">
          <div className="bg-white rounded-lg lg:ml-20">
            {renderCurrentStep()}
            {renderNavigationButtons()}
          </div>
        </div>
      </div>
      <div className="hidden md:flex md:w-3/5 relative overflow-hidden justify-center items-center bg-white">
        <div
          className="w-full max-w-[700px] aspect-square relative overflow-hidden shadow-sm mx-auto"
          style={{ borderRadius: '20%' }}
        >
          <EsporaiVideo />
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
